<template>
  <div class="bg-background-light text-slate-800 antialiased h-screen flex overflow-hidden">
    <AppSidebar currentPage="tasks" />

    <main class="flex-1 flex flex-col h-full overflow-hidden pt-14 md:pt-0">

      <!-- Header -->
      <header class="bg-surface border-b border-slate-200 flex-shrink-0 px-4 md:px-8 py-3 md:py-0 md:h-16">
        <!-- Desktop: single row -->
        <div class="hidden md:flex items-center justify-between h-full">
          <div>
            <h2 class="text-lg font-bold text-slate-900">Tasks & Workflow</h2>
            <p class="text-xs text-slate-500">Manage team assignments, track progress, and update client statuses.</p>
          </div>
          <div class="flex items-center gap-3">
            <div class="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
              <button @click="activeTab = 'kanban'"
                class="px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5"
                :class="activeTab === 'kanban' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'">
                <span class="material-symbols-outlined" style="font-size:15px;">view_kanban</span>
                Tasks
              </button>
              <button @click="activeTab = 'templates'; loadTemplates()"
                class="px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5"
                :class="activeTab === 'templates' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'">
                <span class="material-symbols-outlined" style="font-size:15px;">dashboard_customize</span>
                Templates
              </button>
            </div>
            <button v-if="activeTab === 'kanban'" @click="showAddTask = true"
              class="px-4 py-2 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover shadow-md shadow-primary/20 transition-all flex items-center gap-2">
              <span class="material-symbols-outlined" style="font-size:18px;">add_task</span>
              Add Task
            </button>
            <button v-else @click="openTemplateModal(null)"
              class="px-4 py-2 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover shadow-md shadow-primary/20 transition-all flex items-center gap-2">
              <span class="material-symbols-outlined" style="font-size:18px;">add</span>
              New Template
            </button>
          </div>
        </div>

      </header>

      <!-- ── KANBAN TAB ── -->
      <div v-if="activeTab === 'kanban'" class="flex-1 overflow-hidden flex flex-col">

        <!-- Mobile tab+action bar -->
        <div class="flex md:hidden items-center gap-2 px-3 py-2 bg-white border-b border-slate-200 flex-shrink-0 z-10">
          <div class="flex items-center gap-1 bg-slate-100 p-1 rounded-lg flex-1">
            <button @click="activeTab = 'kanban'"
              class="flex-1 py-1.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1 bg-white text-slate-900 shadow-sm">
              <span class="material-symbols-outlined" style="font-size:14px;">view_kanban</span>
              Tasks
            </button>
            <button @click="activeTab = 'templates'; loadTemplates()"
              class="flex-1 py-1.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1 text-slate-500">
              <span class="material-symbols-outlined" style="font-size:14px;">dashboard_customize</span>
              Templates
            </button>
          </div>
          <button @click="showAddTask = true"
            class="p-2 rounded-lg bg-primary text-secondary hover:bg-primary-hover transition-all flex-shrink-0">
            <span class="material-symbols-outlined" style="font-size:18px;">add_task</span>
          </button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="flex-1 flex items-center justify-center">
          <svg class="animate-spin h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>

        <div v-else class="flex-1 overflow-y-auto p-3 md:p-6 lg:p-8 relative">

          <!-- Two column layout -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-start">

            <!-- TO-DO COLUMN -->
            <div>
              <div class="flex items-center gap-2 mb-4">
                <span class="h-2.5 w-2.5 rounded-full bg-amber-400"></span>
                <h3 class="text-sm font-bold text-slate-700 uppercase tracking-wide">To-Do</h3>
                <span class="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700">{{ todoClients.length }} clients</span>
              </div>

              <div v-if="todoClients.length === 0" class="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                <span class="material-symbols-outlined text-slate-300" style="font-size:36px;">task_alt</span>
                <p class="text-sm text-slate-400 mt-2">No pending tasks</p>
              </div>

              <div class="space-y-3">
                <div v-for="client in todoClients" :key="'todo-' + client.name"
                  class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  <!-- Client Header -->
                  <div class="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <div class="flex items-center gap-3 min-w-0">
                      <div class="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span class="text-primary font-bold text-sm">{{ client.name.charAt(0).toUpperCase() }}</span>
                      </div>
                      <div class="min-w-0">
                        <p class="text-sm font-bold text-slate-900 truncate">{{ client.name }}</p>
                        <p class="text-xs text-slate-400">{{ client.tasks.length }} task{{ client.tasks.length !== 1 ? "s" : "" }} remaining</p>
                      </div>
                    </div>
                    <button v-if="client.tasks.length > 3"
                      @click="toggleClient('todo', client.name)"
                      class="flex items-center gap-1 text-xs font-semibold text-primary hover:underline flex-shrink-0 ml-3">
                      {{ expandedClients['todo-' + client.name] ? 'Show less' : 'Show all' }}
                      <span class="material-symbols-outlined transition-transform" style="font-size:16px;"
                        :style="expandedClients['todo-' + client.name] ? 'transform:rotate(180deg)' : ''">expand_more</span>
                    </button>
                  </div>
                  <!-- Tasks list -->
                  <div class="divide-y divide-slate-50">
                    <div v-for="task in getVisibleTasks('todo', client)"
                      :key="task.id"
                      class="flex items-start gap-3 px-5 py-3 hover:bg-slate-50 transition-colors group">
                      <!-- Checkbox to complete -->
                      <button @click="markTaskDone(task)"
                        class="mt-0.5 h-4 w-4 rounded border-2 border-slate-300 hover:border-green-500 flex-shrink-0 transition-colors"
                        title="Mark complete"></button>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm text-slate-800 font-medium truncate">{{ task.title }}</p>
                        <div class="flex items-center gap-2 mt-0.5">
                          <span class="text-[10px] px-1.5 py-0.5 rounded font-semibold" :class="getCategoryClass(task.category)">{{ task.category }}</span>
                          <span class="text-[10px] font-semibold" :class="getPriorityClass(task.priority)">{{ task.priority }}</span>
                        </div>
                      </div>
                    </div>
                    <!-- Collapsed count row -->
                    <div v-if="client.tasks.length > 3 && !expandedClients['todo-' + client.name]"
                      class="px-5 py-2.5 bg-slate-50 text-center">
                      <button @click="toggleClient('todo', client.name)" class="text-xs text-slate-400 hover:text-primary font-medium">
                        + {{ client.tasks.length - 3 }} more task{{ client.tasks.length - 3 !== 1 ? "s" : "" }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- COMPLETED COLUMN -->
            <div>
              <div class="flex items-center gap-2 mb-4">
                <span class="h-2.5 w-2.5 rounded-full bg-green-500"></span>
                <h3 class="text-sm font-bold text-slate-700 uppercase tracking-wide">Completed</h3>
                <span class="px-2 py-0.5 rounded-full text-xs font-bold bg-green-50 text-green-700">{{ completedClients.length }} clients</span>
              </div>

              <div v-if="completedClients.length === 0" class="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                <span class="material-symbols-outlined text-slate-300" style="font-size:36px;">check_circle</span>
                <p class="text-sm text-slate-400 mt-2">No completed tasks yet</p>
              </div>

              <div class="space-y-3">
                <div v-for="client in completedClients" :key="'done-' + client.name"
                  class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  <!-- Client Header -->
                  <div class="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                    <div class="flex items-center gap-3 min-w-0">
                      <div class="h-9 w-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <span class="text-green-600 font-bold text-sm">{{ client.name.charAt(0).toUpperCase() }}</span>
                      </div>
                      <div class="min-w-0">
                        <p class="text-sm font-bold text-slate-900 truncate">{{ client.name }}</p>
                        <p class="text-xs text-slate-400">{{ client.tasks.length }} task{{ client.tasks.length !== 1 ? "s" : "" }} completed</p>
                      </div>
                    </div>
                    <button v-if="client.tasks.length > 3"
                      @click="toggleClient('done', client.name)"
                      class="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-primary flex-shrink-0 ml-3">
                      {{ expandedClients['done-' + client.name] ? 'Show less' : 'Show all' }}
                      <span class="material-symbols-outlined transition-transform" style="font-size:16px;"
                        :style="expandedClients['done-' + client.name] ? 'transform:rotate(180deg)' : ''">expand_more</span>
                    </button>
                  </div>
                  <!-- Tasks list -->
                  <div class="divide-y divide-slate-50">
                    <div v-for="task in getVisibleTasks('done', client)"
                      :key="task.id"
                      class="flex items-start gap-3 px-5 py-3">
                      <span class="material-symbols-outlined text-green-500 flex-shrink-0 mt-0.5" style="font-size:16px;">check_circle</span>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm text-slate-400 line-through truncate">{{ task.title }}</p>
                        <div class="flex items-center gap-2 mt-0.5">
                          <span class="text-[10px] px-1.5 py-0.5 rounded font-semibold opacity-60" :class="getCategoryClass(task.category)">{{ task.category }}</span>
                        </div>
                      </div>
                    </div>
                    <!-- Collapsed count row -->
                    <div v-if="client.tasks.length > 3 && !expandedClients['done-' + client.name]"
                      class="px-5 py-2.5 bg-slate-50 text-center">
                      <button @click="toggleClient('done', client.name)" class="text-xs text-slate-400 hover:text-primary font-medium">
                        + {{ client.tasks.length - 3 }} more task{{ client.tasks.length - 3 !== 1 ? "s" : "" }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- ── TEMPLATES TAB ── -->
      <div v-else class="flex-1 overflow-hidden flex flex-col">

        <!-- Mobile tab+action bar -->
        <div class="flex md:hidden items-center gap-2 px-3 py-2 bg-white border-b border-slate-200 flex-shrink-0 z-10">
          <div class="flex items-center gap-1 bg-slate-100 p-1 rounded-lg flex-1">
            <button @click="activeTab = 'kanban'"
              class="flex-1 py-1.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1 text-slate-500">
              <span class="material-symbols-outlined" style="font-size:14px;">view_kanban</span>
              Tasks
            </button>
            <button @click="activeTab = 'templates'; loadTemplates()"
              class="flex-1 py-1.5 rounded-md text-xs font-semibold flex items-center justify-center gap-1 bg-white text-slate-900 shadow-sm">
              <span class="material-symbols-outlined" style="font-size:14px;">dashboard_customize</span>
              Templates
            </button>
          </div>
          <button @click="openTemplateModal(null)"
            class="p-2 rounded-lg bg-primary text-secondary hover:bg-primary-hover transition-all flex-shrink-0">
            <span class="material-symbols-outlined" style="font-size:18px;">add</span>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto p-3 md:p-6 lg:p-8">

        <div v-if="templatesLoading" class="flex items-center justify-center py-20">
          <svg class="animate-spin h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>

        <div v-else-if="templates.length === 0" class="flex flex-col items-center justify-center py-20 text-center">
          <div class="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
            <span class="material-symbols-outlined text-slate-400" style="font-size:32px;">dashboard_customize</span>
          </div>
          <p class="text-slate-700 font-semibold mb-1">No templates yet</p>
          <p class="text-slate-400 text-sm mb-4">Create your first task template to assign to clients.</p>
          <button @click="openTemplateModal(null)"
            class="px-4 py-2 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size:16px;">add</span>
            New Template
          </button>
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div v-for="tpl in templates" :key="tpl.id"
            class="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-primary/30 transition-all flex flex-col">
            <!-- Card Header -->
            <div class="p-5 flex items-start justify-between gap-3">
              <div class="flex items-center gap-3 min-w-0">
                <div class="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span class="material-symbols-outlined text-primary" style="font-size:20px;">dashboard_customize</span>
                </div>
                <div class="min-w-0">
                  <p class="text-sm font-bold text-slate-900 truncate">{{ tpl.name }}</p>
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold mt-1" :class="getEntityClass(tpl.entityType)">
                    {{ tpl.entityType || 'Individual' }}
                  </span>
                </div>
              </div>
              <div class="flex items-center gap-1 flex-shrink-0">
                <button @click="openTemplateModal(tpl)"
                  class="p-1.5 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/10 transition-all" title="Edit">
                  <span class="material-symbols-outlined" style="font-size:18px;">edit</span>
                </button>
                <button @click="confirmDeleteTemplate(tpl)"
                  class="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all" title="Delete">
                  <span class="material-symbols-outlined" style="font-size:18px;">delete</span>
                </button>
              </div>
            </div>
            <!-- Description -->
            <div class="px-5 pb-3 flex-1">
              <p v-if="tpl.description" class="text-xs text-slate-500 leading-relaxed line-clamp-2">{{ tpl.description }}</p>
              <p v-else class="text-xs text-slate-300 italic">No description</p>
            </div>
            <!-- Footer -->
            <div class="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
              <div class="flex items-center gap-1.5 text-xs text-slate-500">
                <span class="material-symbols-outlined" style="font-size:15px;">checklist</span>
                {{ tpl.tasks.length }} task{{ tpl.tasks.length !== 1 ? 's' : '' }}
              </div>
              <button @click="openTemplateModal(tpl)" class="text-xs font-semibold text-primary hover:underline flex items-center gap-1">
                View tasks <span class="material-symbols-outlined" style="font-size:13px;">arrow_forward</span>
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>

    </main>

    <!-- ── ADD TASK MODAL ── -->
    <div v-if="showAddTask" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-5">
          <h3 class="text-base font-bold text-slate-900">Add New Task</h3>
          <button @click="showAddTask = false" class="text-slate-400 hover:text-slate-600"><span class="material-symbols-outlined">close</span></button>
        </div>
        <div class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Task Title</label>
            <input v-model="newTask.title" type="text" placeholder="e.g. Review Tax Return"
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Client Name</label>
            <input v-model="newTask.clientName" type="text" placeholder="Client name"
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Category</label>
              <select v-model="newTask.category" class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                <option>Tax</option><option>Filing</option><option>Documents</option><option>Audit</option>
                <option>Consulting</option><option>Onboarding</option><option>Signature</option><option>Admin</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Priority</label>
              <select v-model="newTask.priority" class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                <option>Urgent</option><option>High</option><option>Medium</option><option>Low</option>
              </select>
            </div>
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description</label>
            <textarea v-model="newTask.description" rows="3" placeholder="Optional description..."
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"></textarea>
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Due Date</label>
            <input v-model="newTask.dueDate" type="date" class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
          <div v-if="addTaskError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{{ addTaskError }}</div>
          <div class="flex gap-3 pt-2">
            <button @click="showAddTask = false" class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button @click="addTask" :disabled="addingTask"
              class="flex-1 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <svg v-if="addingTask" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              {{ addingTask ? 'Adding...' : 'Add Task' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── TEMPLATE CREATE/EDIT MODAL ── -->
    <div v-if="showTemplateModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden" style="max-height:90vh;">
        <div class="flex items-center justify-between px-6 py-5 border-b border-slate-100 flex-shrink-0">
          <div>
            <h3 class="text-base font-bold text-slate-900">{{ editingTemplate ? 'Edit Template' : 'New Template' }}</h3>
            <p class="text-xs text-slate-400 mt-0.5">{{ editingTemplate ? 'Update template details and tasks.' : 'Create a reusable task template for clients.' }}</p>
          </div>
          <button @click="showTemplateModal = false" class="text-slate-400 hover:text-slate-600"><span class="material-symbols-outlined">close</span></button>
        </div>

        <div class="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Template Name *</label>
              <input v-model="templateForm.name" type="text" placeholder="e.g. Individual Tax Return 2025"
                class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Entity Type</label>
              <select v-model="templateForm.entityType" class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                <option>Individual</option><option>Business</option><option>C-Corp</option><option>Trust</option><option>SMSF</option>
              </select>
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description</label>
            <textarea v-model="templateForm.description" rows="2" placeholder="Optional description of this template..."
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"></textarea>
          </div>

          <!-- Tasks -->
          <div>
            <div class="flex items-center justify-between mb-3">
              <label class="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Tasks
                <span class="ml-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-bold normal-case">{{ templateForm.tasks.length }}</span>
              </label>
              <button @click="addTemplateTask" class="flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                <span class="material-symbols-outlined" style="font-size:15px;">add</span> Add Task
              </button>
            </div>

            <div v-if="templateForm.tasks.length === 0" class="text-center py-6 rounded-xl border-2 border-dashed border-slate-200 text-slate-400">
              <span class="material-symbols-outlined text-slate-300" style="font-size:28px;">checklist</span>
              <p class="text-xs mt-1">No tasks yet. Click "Add Task" to get started.</p>
            </div>

            <div v-else class="space-y-2">
              <div v-for="(task, index) in templateForm.tasks" :key="index"
                class="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3">
                <!-- Task title row -->
                <div class="flex items-center gap-2">
                  <input v-model="task.title" type="text" :placeholder="'Task ' + (index + 1) + ' title'"
                    class="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white" />
                  <button @click="removeTemplateTask(index)" class="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0">
                    <span class="material-symbols-outlined" style="font-size:16px;">delete</span>
                  </button>
                </div>
                <!-- Due in days / Priority / Category -->
                <div class="grid grid-cols-3 gap-2">
                  <div>
                    <label class="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Due in days</label>
                    <input v-model.number="task.daysUntilDue" type="number" min="1" placeholder="7"
                      class="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white" />
                  </div>
                  <div>
                    <label class="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Priority</label>
                    <select v-model="task.priority" class="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none bg-white">
                      <option>Urgent</option><option>High</option><option>Medium</option><option>Low</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Category</label>
                    <select v-model="task.category" class="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none bg-white">
                      <option>General</option><option>Tax Prep</option><option>Docs</option><option>Compliance</option><option>Accounting</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="templateError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size:18px;">error</span>{{ templateError }}
          </div>
        </div>

        <div class="flex gap-3 px-6 py-4 border-t border-slate-100 flex-shrink-0">
          <button @click="showTemplateModal = false" class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
          <button @click="saveTemplate" :disabled="savingTemplate"
            class="flex-1 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
            <svg v-if="savingTemplate" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            <span class="material-symbols-outlined" style="font-size:16px;" v-else>save</span>
            {{ savingTemplate ? 'Saving...' : (editingTemplate ? 'Save Changes' : 'Create Template') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── DELETE CONFIRM MODAL ── -->
    <div v-if="showDeleteConfirm" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div class="flex flex-col items-center text-center mb-5">
          <div class="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center mb-3">
            <span class="material-symbols-outlined text-red-500" style="font-size:24px;">delete</span>
          </div>
          <h3 class="text-base font-bold text-slate-900">Delete Template?</h3>
          <p class="text-sm text-slate-500 mt-1">Are you sure you want to delete <strong>{{ templateToDelete?.name }}</strong>? This cannot be undone.</p>
        </div>
        <div class="flex gap-3">
          <button @click="showDeleteConfirm = false" class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button @click="deleteTemplate" :disabled="deletingTemplate"
            class="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
            <svg v-if="deletingTemplate" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            {{ deletingTemplate ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import AppSidebar from '../components/AppSidebar.vue';
export default {
  name: 'AdminTasksWorkflow',
  components: { AppSidebar },
  data() {
    return {
      activeTab: 'kanban',
      // Kanban
      loading: true,
      allTasks: [],
      expandedClients: {},
      showAddTask: false,
      addingTask: false,
      addTaskError: '',
      newTask: { title: '', clientName: '', category: 'Tax', priority: 'Medium', description: '', dueDate: '' },
      // Templates
      templates: [],
      templatesLoading: false,
      templatesLoaded: false,
      showTemplateModal: false,
      savingTemplate: false,
      templateError: '',
      editingTemplate: null,
      templateForm: { name: '', entityType: 'Individual', description: '', tasks: [] },
      // Delete
      showDeleteConfirm: false,
      templateToDelete: null,
      deletingTemplate: false,
    };
  },

  async mounted() {
    await this.loadTasks();
  },

  computed: {
    todoClients() {
      const map = {};
      this.allTasks.filter(t => t.status !== 'done').forEach(t => {
        if (!map[t.clientName]) map[t.clientName] = { name: t.clientName, tasks: [] };
        map[t.clientName].tasks.push(t);
      });
      return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
    },
    completedClients() {
      const map = {};
      this.allTasks.filter(t => t.status === 'done').forEach(t => {
        if (!map[t.clientName]) map[t.clientName] = { name: t.clientName, tasks: [] };
        map[t.clientName].tasks.push(t);
      });
      return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
    }
  },

  methods: {
    // ── Kanban ─────────────────────────────────────────────
    async loadTasks() {
      try {
        this.loading = true;
        const headers = await this.$sharepoint.getAuthHeaders();
        const url = `${this.$sharepoint.baseUrl}/getbytitle('Client Tasks')/items?$select=Id,Title,Description,ClientName,TaskStatus,Category,Priority,DueDate&$orderby=Created desc&$top=500`;
        const response = await fetch(url, { headers });
        const data = await response.json();
        const results = data.d?.results || [];
        const doneStatuses = ['Completed', 'Done', 'Filed'];
        this.allTasks = results.map(item => ({
          id: item.Id,
          title: item.Title,
          description: item.Description || '',
          clientName: item.ClientName || 'Unknown',
          category: item.Category || 'General',
          priority: item.Priority || 'Medium',
          dueDate: item.DueDate,
          status: doneStatuses.includes(item.TaskStatus) ? 'done' : 'todo'
        }));
      } catch (error) {
        console.error('Error loading tasks:', error);
      } finally {
        this.loading = false;
      }
    },

    async addTask() {
      this.addTaskError = '';
      if (!this.newTask.title.trim()) { this.addTaskError = 'Task title is required.'; return; }
      if (!this.newTask.clientName.trim()) { this.addTaskError = 'Client name is required.'; return; }
      this.addingTask = true;
      try {
        await this.$sharepoint.addTask({
          title: this.newTask.title, clientName: this.newTask.clientName,
          category: this.newTask.category, description: this.newTask.description,
          priority: this.newTask.priority, dueDate: this.newTask.dueDate || null,
          status: 'To-Do'
        });
        this.showAddTask = false;
        this.newTask = { title: '', clientName: '', category: 'Tax', priority: 'Medium', description: '', dueDate: '' };
        await this.loadTasks();
      } catch (error) {
        this.addTaskError = 'Failed to add task. Please try again.';
      } finally {
        this.addingTask = false;
      }
    },

    async markTaskDone(task) {
      // Optimistic update
      task.status = 'done';
      try {
        const headers = await this.$sharepoint.getAuthHeaders();
        const formDigest = await this.$sharepoint.getFormDigest();
        const url = `${this.$sharepoint.baseUrl}/getbytitle('Client Tasks')/items(${task.id})`;
        await fetch(url, {
          method: 'POST',
          headers: { ...headers, 'X-RequestDigest': formDigest, 'X-HTTP-Method': 'MERGE', 'IF-MATCH': '*' },
          body: JSON.stringify({ __metadata: { type: 'SP.Data.Client_x0020_TasksListItem' }, TaskStatus: 'Completed' })
        });
      } catch (e) {
        task.status = 'todo'; // revert on failure
        console.error('Failed to mark task done:', e);
      }
    },

    toggleClient(col, name) {
      const key = col + '-' + name;
      this.expandedClients = { ...this.expandedClients, [key]: !this.expandedClients[key] };
    },

    getVisibleTasks(col, client) {
      const key = col + '-' + client.name;
      if (client.tasks.length <= 3 || this.expandedClients[key]) return client.tasks;
      return client.tasks.slice(0, 3);
    },

    // ── Templates ──────────────────────────────────────────
    async loadTemplates() {
      if (this.templatesLoaded) return;
      this.templatesLoading = true;
      try {
        this.templates = await this.$sharepoint.getTemplates();
        this.templatesLoaded = true;
      } catch (e) {
        console.error('Error loading templates:', e);
      } finally {
        this.templatesLoading = false;
      }
    },

    openTemplateModal(template) {
      this.templateError = '';
      if (template) {
        this.editingTemplate = template;
        this.templateForm = {
          name: template.name,
          entityType: template.entityType || 'Individual',
          description: template.description || '',
          tasks: template.tasks.map(t => ({
            ...t,
            daysUntilDue: t.daysUntilDue || t.dueDays || 7
          }))
        };
      } else {
        this.editingTemplate = null;
        this.templateForm = { name: '', entityType: 'Individual', description: '', tasks: [] };
      }
      this.showTemplateModal = true;
    },

    addTemplateTask() {
      this.templateForm.tasks.push({ title: '', category: 'General', priority: 'Medium', daysUntilDue: 7 });
    },

    removeTemplateTask(index) {
      this.templateForm.tasks.splice(index, 1);
    },

    async saveTemplate() {
      this.templateError = '';
      if (!this.templateForm.name.trim()) { this.templateError = 'Template name is required.'; return; }
      const invalidTask = this.templateForm.tasks.find(t => !t.title.trim());
      if (invalidTask) { this.templateError = 'All tasks must have a title.'; return; }
      this.savingTemplate = true;
      try {
        const payload = {
          name: this.templateForm.name.trim(),
          entityType: this.templateForm.entityType,
          description: this.templateForm.description.trim(),
          tasks: this.templateForm.tasks
        };
        if (this.editingTemplate) {
          await this.$sharepoint.updateTemplate(this.editingTemplate.id, payload);
          const idx = this.templates.findIndex(t => t.id === this.editingTemplate.id);
          if (idx !== -1) this.templates.splice(idx, 1, { ...this.editingTemplate, ...payload });
        } else {
          const result = await this.$sharepoint.saveTemplate(payload);
          this.templates.push({ id: result.Id, ...payload });
        }
        this.showTemplateModal = false;
      } catch (e) {
        this.templateError = 'Failed to save template. Please try again.';
        console.error(e);
      } finally {
        this.savingTemplate = false;
      }
    },

    confirmDeleteTemplate(template) {
      this.templateToDelete = template;
      this.showDeleteConfirm = true;
    },

    async deleteTemplate() {
      if (!this.templateToDelete) return;
      this.deletingTemplate = true;
      try {
        await this.$sharepoint.deleteTemplate(this.templateToDelete.id);
        this.templates = this.templates.filter(t => t.id !== this.templateToDelete.id);
        this.showDeleteConfirm = false;
        this.templateToDelete = null;
      } catch (e) {
        console.error('Delete error:', e);
      } finally {
        this.deletingTemplate = false;
      }
    },

    // ── Helpers ────────────────────────────────────────────
    getEntityClass(type) {
      const map = { 'Individual': 'bg-blue-50 text-blue-700', 'Business': 'bg-amber-50 text-amber-700', 'C-Corp': 'bg-purple-50 text-purple-700', 'Trust': 'bg-teal-50 text-teal-700', 'SMSF': 'bg-green-50 text-green-700' };
      return map[type] || 'bg-slate-100 text-slate-600';
    },
    getCategoryClass(category) {
      const map = { 'Tax': 'bg-yellow-50 text-yellow-700', 'Filing': 'bg-blue-50 text-blue-700', 'Documents': 'bg-indigo-50 text-indigo-700', 'Audit': 'bg-red-50 text-red-700', 'Consulting': 'bg-teal-50 text-teal-700', 'Onboarding': 'bg-green-50 text-green-700', 'Signature': 'bg-pink-50 text-pink-700', 'Admin': 'bg-slate-100 text-slate-700' };
      return map[category] || 'bg-slate-100 text-slate-600';
    },
    getPriorityClass(priority) {
      const map = { 'Urgent': 'text-red-600', 'High': 'text-orange-500', 'Medium': 'text-amber-500', 'Low': 'text-slate-400' };
      return map[priority] || 'text-slate-400';
    }
  }
};
</script>